import React, { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';

import { loadStripe } from '@stripe/stripe-js';
import './App.css';
import RouterMain from './routes';
import { Elements } from '@stripe/react-stripe-js';
import { RouterProvider, useLocation } from 'react-router-dom';
import Modal from './components/Common/modal/Modal';
import TermsAndConditions from './components/Common/TermsAndConditions';
import { setUser, setUserLoader } from './store/userSlice';
import { AppDispatch, RootState } from './store/store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlansToSubscribe, monitorBookData } from './store/thunks/app.thunk';
import { autoFetchUserDetails, fetchAllArtStyles, fetchTrainingVideos } from './store/thunks/user.thunk';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import {
  loadChatBot,
  loadClarity,
  loadCookieEyes,
  loadGoogleAnalytics,
  loadGoogleApiPlaces,
  loadGoogleTagManager,
} from './services/scripts.service';
import SelectPlanPurchase from './components/SelectPlanPurchase/SelectPlanPurchase';
import {
  openPlanPurchaseModal,
  openStudentBooksCompletedPopup,
  setBeforeUnloadEvent,
  setShowShareModalData,
} from './store/appSlice';
import FlipBookShare from './pages/BookFlip/components/BookFlipShare';
import ReactGA from 'react-ga4';
import { initGA, trackEvent } from './services/analytics.service';
import StudentBooksCompleted from './components/Common/StudentBooksCompleted';
import FlyerGenerationSnackbar from './components/Common/FlyerGenerationSnackbar';
import { UserRoleEnum } from './interfaces/user.interface';

declare global {
  interface Window {
    $crisp?: any[] | undefined;
    CRISP_WEBSITE_ID?: string;
    introJs?: any;
    hj?: any;
  }
}

const {
  REACT_APP_LOAD_COOKIE_EYES,
  REACT_APP_FIREBASE_MODE,
  REACT_APP_STRIPE_PUB_KEY_TEST,
  REACT_APP_STRIPE_PUB_KEY_LIVE,
  REACT_APP_CLARITY_ID,
  REACT_APP_GTM_ID,
  REACT_APP_GA_ID,
  REACT_APP_PLACES_API_KEY,
} = process.env;

const TrackPage = () => {
  const location = useLocation();
  useEffect(() => {
    ReactGA?.send && ReactGA.send({ hitType: 'pageview', page: location.pathname });
  }, [location]);
  return null;
};

function App() {
  const user = useSelector((state: RootState) => state.user.user);
  const isUserLoading = useSelector((state: RootState) => state.user.isUserLoading);
  const bookId = useSelector((state: RootState) => state.app.bookId);
  const bookDataModified = useSelector((state: RootState) => state.app.bookDataModified);
  const isTitleModalOpen = useSelector((state: RootState) => state.app.isTitleModalOpen);
  const currentPathname = useSelector((state: RootState) => state.app.currentPathname);
  const isPlanPurchaseModalVisible = useSelector((state: RootState) => state.app.isPlanPurchaseModalVisible);
  const openIsStudentBooksCompleted = useSelector((state: RootState) => state.app.openIsStudentBooksCompleted);
  const showShareModalData = useSelector((state: RootState) => state.app.showShareModalData);
  const planPurchaseModalData = useSelector((state: RootState) => state.app.planPurchaseModalData);
  const listPlansToSubscribe = useSelector((state: RootState) => state.app.listPlansToSubscribe);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [identifiedUser, setIdentifiedUser] = useState<boolean>(false);
  const [acceptTermsAndConditionsSession, setAcceptTermsAndConditionsSession] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (currentPathname) {
      loadChatBot((user && user.role === UserRoleEnum.student) || !!currentPathname?.includes('auth/student'));
    }
  }, [currentPathname, user]);

  useEffect(() => {
    if (REACT_APP_LOAD_COOKIE_EYES === 'true') {
      loadCookieEyes();
    }

    dispatch(fetchTrainingVideos());
    dispatch(fetchAllArtStyles());

    /// todo: need to remove hardcoded key after prod migration to new environment
    const clarityKey = REACT_APP_FIREBASE_MODE !== 'development' ? 'pmloobrzm2' : REACT_APP_CLARITY_ID;

    loadClarity(clarityKey);
    loadGoogleTagManager(REACT_APP_GTM_ID!);
    loadGoogleApiPlaces(REACT_APP_PLACES_API_KEY!);
    loadGoogleAnalytics(REACT_APP_GA_ID!);
    initGA(process.env.REACT_APP_GA_ID!);

    dispatch(setUserLoader(true));
    dispatch(autoFetchUserDetails());
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (target.tagName === 'BUTTON') {
        const buttonText = target.innerText || 'Unnamed Button';
        const buttonTitle = target.getAttribute('title') || 'No Title';
        trackEvent('Button', 'Click', `${buttonText} - ${buttonTitle}`);
      }

      if (target.tagName === 'A' && target.closest('nav')) {
        trackEvent('Navigation', 'Click', target.innerText || 'Unnamed Link');
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const stripePublicKey =
    REACT_APP_FIREBASE_MODE !== 'development' ? REACT_APP_STRIPE_PUB_KEY_LIVE : REACT_APP_STRIPE_PUB_KEY_TEST;

  // Load the Stripe promise
  const stripePromise = loadStripe(stripePublicKey!);

  const handleGlobalClick = () => {
    user && dispatch(setUser({ ...user, closeTermsAndConditions: false }));
  };

  useEffect(() => {
    if (user?.email && window.hj && !identifiedUser) {
      try {
        console.log('HOTJAR - update tracking');
        window.hj('identify', user.email, {
          email: user.email,
          name: [user?.firstName, user?.lastName]
            .filter((item) => !!item?.trim())
            .join(' ')
            .slice(0, 50),
          role: user?.role || UserRoleEnum.user,
        });
        setIdentifiedUser(true);
      } catch (err) {
        console.log(err);
      }
    }
  }, [user, identifiedUser]);

  useEffect(() => {
    // Show modal if terms are not accepted
    if (!user?.email || (!!user?.uid && !!user?.auth)) {
      return;
    }
    if (!user.plan || !!user.isTrialFreeAccess) {
      if (listPlansToSubscribe === null) {
        dispatch(fetchPlansToSubscribe());
      }
    } else if (!user?.closeTermsAndConditions) {
      const timerId = setTimeout(() => {
        if (['#/purchase/success'].includes(window.location.hash)) {
          return;
        }
        // if (user?.role !== UserRoleEnum.student) {
        //   setIsModalVisible(!user?.acceptedTermsAndConditions);
        // }
      }, 1000);
      return () => clearTimeout(timerId);
    }

    setIsModalVisible(false);
    if (user && !user.acceptedTermsAndConditions) {
      setAcceptTermsAndConditionsSession(true);
      document.addEventListener('click', handleGlobalClick);

      return () => {
        setAcceptTermsAndConditionsSession(false);
        document.removeEventListener('click', handleGlobalClick);
      };
    }
    if (!!user?.acceptedTermsAndConditions && acceptTermsAndConditionsSession) {
      setAcceptTermsAndConditionsSession(false);
      document.removeEventListener('click', handleGlobalClick);
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = dispatch(monitorBookData());

    return () => {
      if (unsubscribe instanceof Function) {
        unsubscribe();
      }
    };
  }, [bookId, bookDataModified, isTitleModalOpen]);

  return (
    <>
      <Elements stripe={stripePromise}>
        {isModalVisible && (
          <Modal
            title={`This is a BETA. Please agree to the terms and conditions of \n the Non-Disclosure Agreement (NDA)`}
            className={'global-absolute-popup-style'}
            showSubmitButton={false}
            showCloseButton={false}
          >
            <TermsAndConditions></TermsAndConditions>
          </Modal>
        )}
        {user && isPlanPurchaseModalVisible && (
          <Modal
            title={planPurchaseModalData?.title}
            className={'purchase-plan-modal'}
            showSubmitButton={false}
            showCloseButton={false}
            showTopRightCloseButton={!planPurchaseModalData?.unclosable}
            showCloseButtonFunction={() => dispatch(openPlanPurchaseModal({ show: false }))}
          >
            <SelectPlanPurchase
              closePlanPurchaseModal={() => dispatch(openPlanPurchaseModal({ show: false }))}
              description={planPurchaseModalData?.description}
              showLogOut={planPurchaseModalData?.showLogOut}
            ></SelectPlanPurchase>
          </Modal>
        )}
        {user && openIsStudentBooksCompleted && (
          <Modal title={''} className={'global-absolute-popup-style'} showSubmitButton={false} showCloseButton={false}>
            <StudentBooksCompleted
              submit={() => {
                dispatch(openStudentBooksCompletedPopup({ show: false }));
              }}
            ></StudentBooksCompleted>
          </Modal>
        )}
        {showShareModalData && (
          <Modal
            title={`Share this story with your friends`}
            className={'global-absolute-popup-style'}
            showSubmitButton={false}
            showCloseButton={false}
            showTopRightCloseButton={true}
            showCloseButtonFunction={() => dispatch(setShowShareModalData(null))}
          >
            <FlipBookShare
              bookData={showShareModalData}
              close={() => dispatch(setShowShareModalData(null))}
            ></FlipBookShare>
          </Modal>
        )}
        <div className={`App bg-white min-h-screen ${isUserLoading ? 'fixed w-full' : ''}`}>
          {isUserLoading && (
            <div className="h-[100vh] flex justify-center items-center">
              <img className="w-16 h-16" src="/loading.gif" alt="Loading" />
            </div>
          )}
          <RouterProvider router={RouterMain} />
        </div>
        <FlyerGenerationSnackbar />
        <Toaster position="bottom-left" />
      </Elements>
    </>
  );
}

export default App;
